interface PrefetchConfig {
  enabled: boolean;
  networkThreshold: 'slow-2g' | '2g' | '3g' | '4g';
}

interface PrefetchEntry {
  module: string;
  timestamp: number;
  status: 'pending' | 'loading' | 'loaded' | 'error';
  promise?: Promise<any>;
}

class PrefetchManager {
  private prefetchMap: Map<string, PrefetchEntry> = new Map();
  private config: PrefetchConfig = {
    enabled: true,
    networkThreshold: '3g'
  };

  private networkSpeedOrder = ['slow-2g', '2g', '3g', '4g'];

  configure(config: Partial<PrefetchConfig>) {
    this.config = { ...this.config, ...config };
  }

  isNetworkSufficient(): boolean {
    if (typeof window === 'undefined' || !('connection' in navigator)) {
      return true;
    }

    const connection = (navigator as any).connection;
    if (!connection || !connection.effectiveType) {
      return true;
    }

    const currentSpeed = connection.effectiveType;
    const thresholdIndex = this.networkSpeedOrder.indexOf(this.config.networkThreshold);
    const currentIndex = this.networkSpeedOrder.indexOf(currentSpeed);

    return currentIndex >= thresholdIndex;
  }

  shouldPrefetch(module: string): boolean {
    if (!this.config.enabled) {
      return false;
    }

    if (!this.isNetworkSufficient()) {
      console.log(`[PrefetchManager] Skipping ${module} - network too slow`);
      return false;
    }

    const entry = this.prefetchMap.get(module);
    if (entry && (entry.status === 'loading' || entry.status === 'loaded')) {
      return false;
    }

    return true;
  }

  async prefetch(module: string, loader: () => Promise<any>): Promise<void> {
    if (!this.shouldPrefetch(module)) {
      return;
    }

    console.log(`[PrefetchManager] Prefetching ${module}`);

    const entry: PrefetchEntry = {
      module,
      timestamp: Date.now(),
      status: 'loading'
    };

    const promise = loader()
      .then((result) => {
        const existingEntry = this.prefetchMap.get(module);
        if (existingEntry) {
          existingEntry.status = 'loaded';
        }
        console.log(`[PrefetchManager] Successfully prefetched ${module}`);
        return result;
      })
      .catch((error) => {
        const existingEntry = this.prefetchMap.get(module);
        if (existingEntry) {
          existingEntry.status = 'error';
        }
        console.error(`[PrefetchManager] Failed to prefetch ${module}:`, error);
        throw error;
      });

    entry.promise = promise;
    this.prefetchMap.set(module, entry);

    await promise;
  }

  getPrefetchStatus(module: string): 'not-started' | 'loading' | 'loaded' | 'error' {
    const entry = this.prefetchMap.get(module);
    if (!entry) return 'not-started';
    return entry.status === 'pending' ? 'not-started' : entry.status;
  }

  clearPrefetches() {
    this.prefetchMap.clear();
    console.log('[PrefetchManager] Cleared all prefetch entries');
  }

  getStats() {
    const stats = {
      total: this.prefetchMap.size,
      loaded: 0,
      loading: 0,
      error: 0,
      pending: 0
    };

    this.prefetchMap.forEach(entry => {
      stats[entry.status]++;
    });

    return stats;
  }
}

export const prefetchManager = new PrefetchManager();
